/**
 * Frontend Error Capture Service
 * ================================
 * Captures and queues frontend JavaScript errors for submission to backend with retry logic.
 *
 * Features:
 * - Global error handlers (window.onerror, onunhandledrejection)
 * - localStorage queue for persistence across page refreshes
 * - 5 retries with 10-second delay between each attempt
 * - Browser info parsing
 * - React ErrorBoundary compatible
 */

import apiClient from './apiClient';
import { getUser } from '../utils/localStorageManager';

interface BrowserInfo {
  user_agent: string;
  browser: string;
  version: string;
  os: string;
}

interface FrontendError {
  error_type: string;
  error_message: string;
  stack_trace?: string;
  component_name?: string;
  page_url: string;
  browser_info: BrowserInfo;
  timestamp: string;
  user_email?: string;
  retry_count?: number;
}

const ERROR_QUEUE_KEY = 'naptune_error_queue';
const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 10000; // 10 seconds
const MAX_QUEUE_SIZE = 100;
const MAX_MESSAGE_LENGTH = 5000;
const MAX_STACK_LENGTH = 10000;

class ErrorCaptureService {
  private isProcessing = false;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;

  /**
   * Initialize error capture - sets up global handlers.
   * Should be called once at app startup.
   */
  public initialize(): void {
    if (this.initialized) {
      return;
    }

    // Global JS error handler
    window.onerror = (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ): boolean => {
      // Handle CORS-blocked "Script error." with enhanced context
      if (message === 'Script error.' && !source) {
        const user = getUser();
        const contextInfo = {
          url: window.location.href,
          path: window.location.pathname,
          user_email: user?.email || 'anonymous',
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          // Check what external scripts might be the cause
          paypal_sdk_loaded: !!window.paypal,
          active_element: document.activeElement?.tagName || 'unknown',
          active_element_id: document.activeElement?.id || '',
          // Browser context
          viewport_width: window.innerWidth,
          viewport_height: window.innerHeight,
          // Page state
          page_visible: document.visibilityState === 'visible',
          focus_within: document.hasFocus(),
        };

        // Log with full context for backend analysis
        this.captureError({
          error_type: 'ScriptError_CORS',
          error_message: `CORS-blocked script error | Path: ${contextInfo.path} | User: ${contextInfo.user_email} | PayPal: ${contextInfo.paypal_sdk_loaded}`,
          stack_trace: JSON.stringify(contextInfo, null, 2),
          page_url: window.location.href,
          component_name: contextInfo.path,
        });

        // Suppress the error to prevent React's dev overlay from showing
        // These are third-party script errors (PayPal, etc.) that we can't fix
        return true;
      }

      this.captureError({
        error_type: 'UncaughtError',
        error_message: this.truncateString(String(message), MAX_MESSAGE_LENGTH),
        stack_trace: error?.stack ? this.truncateString(error.stack, MAX_STACK_LENGTH) : undefined,
        page_url: window.location.href,
      });
      return false; // Don't prevent default handling
    };

    // Unhandled promise rejection handler
    window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
      const error = event.reason;
      this.captureError({
        error_type: 'UnhandledPromiseRejection',
        error_message: this.truncateString(
          error?.message || String(error),
          MAX_MESSAGE_LENGTH
        ),
        stack_trace: error?.stack ? this.truncateString(error.stack, MAX_STACK_LENGTH) : undefined,
        page_url: window.location.href,
      });
    };

    this.initialized = true;

    // Process any queued errors on startup
    this.processQueue();
  }

  /**
   * Capture an error and queue for submission.
   */
  public captureError(errorData: Partial<FrontendError>): void {
    const user = getUser();
    const error: FrontendError = {
      error_type: errorData.error_type || 'Unknown',
      error_message: errorData.error_message || 'Unknown error',
      stack_trace: errorData.stack_trace,
      component_name: errorData.component_name,
      page_url: errorData.page_url || window.location.href,
      browser_info: this.getBrowserInfo(),
      timestamp: new Date().toISOString(),
      user_email: user?.email,
      retry_count: 0,
    };

    this.addToQueue(error);
    this.processQueue();
  }

  /**
   * Capture a React error from ErrorBoundary.
   * Compatible with componentDidCatch signature.
   */
  public captureReactError(
    error: Error,
    componentStack: string,
    componentName?: string
  ): void {
    const fullStackTrace = error.stack
      ? `${error.stack}\n\nComponent Stack:${componentStack}`
      : `Component Stack:${componentStack}`;

    this.captureError({
      error_type: 'ReactError',
      error_message: this.truncateString(error.message, MAX_MESSAGE_LENGTH),
      stack_trace: this.truncateString(fullStackTrace, MAX_STACK_LENGTH),
      component_name: componentName,
    });
  }

  /**
   * Manually report an error (for use in try-catch blocks).
   */
  public reportError(
    error: Error,
    context?: { componentName?: string; additionalInfo?: string }
  ): void {
    let stackTrace = error.stack;
    if (context?.additionalInfo && stackTrace) {
      stackTrace = `${stackTrace}\n\nAdditional Info: ${context.additionalInfo}`;
    }

    this.captureError({
      error_type: error.name || 'Error',
      error_message: this.truncateString(error.message, MAX_MESSAGE_LENGTH),
      stack_trace: stackTrace ? this.truncateString(stackTrace, MAX_STACK_LENGTH) : undefined,
      component_name: context?.componentName,
    });
  }

  private getBrowserInfo(): BrowserInfo {
    const ua = navigator.userAgent;
    return {
      user_agent: ua,
      browser: this.parseBrowser(ua),
      version: this.parseBrowserVersion(ua),
      os: this.parseOS(ua),
    };
  }

  private parseBrowser(ua: string): string {
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
    return 'Unknown';
  }

  private parseBrowserVersion(ua: string): string {
    const patterns = [
      /Firefox\/(\d+)/,
      /Edg\/(\d+)/,
      /Chrome\/(\d+)/,
      /Safari\/(\d+)/,
      /OPR\/(\d+)/,
      /Opera\/(\d+)/,
    ];

    for (const pattern of patterns) {
      const match = ua.match(pattern);
      if (match) return match[1];
    }
    return 'Unknown';
  }

  private parseOS(ua: string): string {
    if (ua.includes('Windows NT 10')) return 'Windows 10/11';
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS X')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown';
  }

  private truncateString(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength) + '... [truncated]';
  }

  private addToQueue(error: FrontendError): void {
    const queue = this.getQueue();
    if (queue.length >= MAX_QUEUE_SIZE) {
      queue.shift(); // Remove oldest if at capacity
    }
    queue.push(error);
    this.saveQueue(queue);
  }

  private getQueue(): FrontendError[] {
    try {
      const stored = localStorage.getItem(ERROR_QUEUE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private saveQueue(queue: FrontendError[]): void {
    try {
      localStorage.setItem(ERROR_QUEUE_KEY, JSON.stringify(queue));
    } catch {
      // localStorage might be full, clear old errors
      localStorage.removeItem(ERROR_QUEUE_KEY);
    }
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing) return;
    this.isProcessing = true;

    const queue = this.getQueue();
    const remaining: FrontendError[] = [];
    let hasFailures = false;

    for (const error of queue) {
      const success = await this.submitError(error);
      if (!success) {
        error.retry_count = (error.retry_count || 0) + 1;
        if (error.retry_count < MAX_RETRIES) {
          remaining.push(error);
          hasFailures = true;
        }
        // If max retries exceeded, error is dropped (not added to remaining)
      }
    }

    this.saveQueue(remaining);
    this.isProcessing = false;

    // Schedule retry if there are remaining errors
    if (hasFailures && remaining.length > 0) {
      this.scheduleRetry();
    }
  }

  private async submitError(error: FrontendError): Promise<boolean> {
    try {
      await apiClient.post('/errors/frontend-errors', {
        error_type: error.error_type,
        error_message: error.error_message,
        stack_trace: error.stack_trace,
        component_name: error.component_name,
        page_url: error.page_url,
        browser_info: error.browser_info,
        timestamp: error.timestamp,
        user_email: error.user_email,
      });
      return true;
    } catch {
      return false;
    }
  }

  private scheduleRetry(): void {
    // Don't schedule if already scheduled
    if (this.retryTimeout) return;

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.processQueue();
    }, RETRY_DELAY_MS);
  }

  /**
   * Get the current queue length (for debugging/monitoring).
   */
  public getQueueLength(): number {
    return this.getQueue().length;
  }

  /**
   * Clear the error queue (for testing/debugging).
   */
  public clearQueue(): void {
    localStorage.removeItem(ERROR_QUEUE_KEY);
  }
}

// Export singleton instance
export const errorCaptureService = new ErrorCaptureService();
